import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { enqueueWrapAffectedTextFrameSaves } from '../handlers/enqueue-wrap-affected-text-frame-saves'
import type { HomeDocumentLayout, HomeFoundation, HomePersistence } from '../types/home-groups'

export type ResaveWrapAffectedTextFrames = (
  commits: readonly { before: ShapeData; after: ShapeData }[],
  historyTxnId: string | undefined,
  alreadySavedIds: ReadonlySet<string>
) => void

/**
 * Binds `enqueueWrapAffectedTextFrameSaves` to the home editor's live shapes, chains, bake context
 * and queue, for the commit handlers and the undo/redo step. Reads the manager at call time:
 * `getShapes()` is already up to date inside a commit, while the memoized wrap context is not.
 */
export function useWrapAffectedTextFrameResave(
  deps: Pick<
    HomeFoundation,
    'textChains' | 'shapeManagerRef' | 'shapeTextBakeRef' | 'fontLookupRef'
  > &
    Pick<HomeDocumentLayout, 'spreadProjection'> &
    Pick<HomePersistence, 'enqueueUpdate' | 'markEdited'>
): ResaveWrapAffectedTextFrames {
  const {
    textChains,
    shapeManagerRef,
    shapeTextBakeRef,
    fontLookupRef,
    spreadProjection,
    enqueueUpdate,
    markEdited,
  } = deps

  return useCallback(
    (commits, historyTxnId, alreadySavedIds) => {
      const mgr = shapeManagerRef.current

      if (!mgr) return

      enqueueWrapAffectedTextFrameSaves(commits, historyTxnId, alreadySavedIds, {
        shapes: mgr.getShapes(),
        zIndexOf: mgr.getShapeTextWrapContext()?.zIndexOf ?? (() => undefined),
        textChains,
        resolveBake: shapeTextBakeRef.current,
        projection: spreadProjection,
        fonts: fontLookupRef.current ?? undefined,
        enqueueUpdate,
        markEdited,
      })
    },
    [
      enqueueUpdate,
      fontLookupRef,
      markEdited,
      shapeManagerRef,
      shapeTextBakeRef,
      spreadProjection,
      textChains,
    ]
  )
}
